import React, { useCallback, useEffect, useRef } from "react";

const LOAD_MORE_THRESHOLD_PX = 48;

type UseInfiniteListboxArgs = {
  items: unknown[];
  hasMore: boolean;
  loading: boolean;
  onLoadMore?: () => void;
};

type UseInfiniteListboxResult = {
  listboxProps: { onScroll: (event: React.UIEvent<HTMLElement>) => void };
  reset: () => void;
};

const useInfiniteListbox = ({
  items,
  hasMore,
  loading,
  onLoadMore,
}: UseInfiniteListboxArgs): UseInfiniteListboxResult => {
  const listboxRef = useRef<HTMLElement | null>(null);
  const restoreScrollTopRef = useRef<number | null>(null);

  useEffect(() => {
    const listbox = listboxRef.current;
    const scrollTop = restoreScrollTopRef.current;
    if (!listbox || scrollTop === null) return;

    restoreScrollTopRef.current = null;
    listbox.scrollTop = scrollTop;
  }, [items]);

  const onScroll = useCallback(
    (event: React.UIEvent<HTMLElement>) => {
      const listbox = event.currentTarget;
      listboxRef.current = listbox;

      if (!hasMore || loading || !onLoadMore) return;

      const { scrollTop, clientHeight, scrollHeight } = listbox;
      if (scrollTop + clientHeight >= scrollHeight - LOAD_MORE_THRESHOLD_PX) {
        restoreScrollTopRef.current = scrollTop;
        onLoadMore();
      }
    },
    [hasMore, loading, onLoadMore]
  );

  const reset = useCallback(() => {
    listboxRef.current = null;
    restoreScrollTopRef.current = null;
  }, []);

  return { listboxProps: { onScroll }, reset };
};

export default useInfiniteListbox;
